import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import { Layers, Loader2 } from "lucide-react";
import { MediaPreview } from "@nexus/ui";
import type { DrilldownSortMode } from "../stores/drilldownFilters.store";
import { segmentLabel, SEGMENT_NONE } from "../stores/drilldownFilters.store";
import { formatBrandName } from "@/features/dashboard/lib/format.js";
import type { CarouselSlide } from "@/features/opportunities/types/opportunities.types";
import { GCS_BASE, extractString, formatYmd, parseCarouselSlides } from "../lib/drilldown-row";
import {
  THUMB_SIZE,
  CHART_HEIGHT,
  CHART_MARGIN,
  XAXIS_HEIGHT,
  YAXIS_WIDTH,
  DrilldownActiveBar,
  StickyYAxisChart,
  computeYAxisTicks,
  useChartContainerWidth,
  useChartScrollPagination,
  useStickyChartWidth,
} from "../lib/drilldown-chart-shared";


interface DrilldownChartViewProps {
  rows: Array<Record<string, unknown>>;
  sortMode: DrilldownSortMode;
  segment1: string;
  segment2: string;
  segment3: string;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onBarDrawerClick?: (row: Record<string, unknown>, index: number) => void;
}

type ChartRow = {
  label: string;
  value: number;
  competitor: string;
  minDate: string;
  maxDate: string;
  thumbnailUrl: string;
  assetUrl: string;
  isVideo: boolean;
  isCarousel: boolean;
  carouselSlides: CarouselSlide[] | null;
  groupby1: string;
  groupby2: string;
  groupby3: string;
};


const METRIC_FIELD: Record<DrilldownSortMode, string> = {
  "Days/Assets": "DaysPerAsset",
  Days: "Days",
  Assets: "Assets",
  Date: "Days",
};

const METRIC_LABEL: Record<DrilldownSortMode, string> = {
  "Days/Assets": "Days/Assets",
  Days: "Days",
  Assets: "Assets",
  Date: "Days",
};

function formatYTick(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}


interface DrilldownBarTooltipProps extends TooltipContentProps<number, string> {
  metricLabel: string;
  segment1: string;
  segment2: string;
  segment3: string;
}

function DrilldownBarTooltip({
  active,
  payload,
  metricLabel,
  segment1,
  segment2,
  segment3,
}: DrilldownBarTooltipProps) {
  if (!active || !payload?.[0]) return null;
  const row = payload[0].payload as ChartRow;

  const groupbys: { label: string; value: string }[] = [];
  if (segment1 !== SEGMENT_NONE && row.groupby1)
    groupbys.push({ label: segmentLabel(segment1), value: row.groupby1 });
  if (segment2 !== SEGMENT_NONE && row.groupby2)
    groupbys.push({ label: segmentLabel(segment2), value: row.groupby2 });
  if (segment3 !== SEGMENT_NONE && row.groupby3)
    groupbys.push({ label: segmentLabel(segment3), value: row.groupby3 });

  return (
    <div className="max-w-[300px] rounded-lg border bg-card p-3 text-xs shadow-md">
      {row.competitor && (
        <p className="mb-2 font-medium text-foreground">{formatBrandName(row.competitor)}</p>
      )}
      {groupbys.map(({ label, value }) => (
        <div key={label} className="mb-1 flex flex-col gap-0.5">
          <span className="text-muted-foreground">{label}</span>
          <span className="truncate text-foreground">{value}</span>
        </div>
      ))}
      <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
        <span className="text-muted-foreground">{metricLabel}:</span>
        <span className="font-medium text-foreground">{formatYTick(row.value)}</span>
      </div>
      {(row.minDate || row.maxDate) && (
        <p className="mt-1 text-muted-foreground">
          {formatYmd(row.minDate)} – {formatYmd(row.maxDate)}
        </p>
      )}
    </div>
  );
}


export function DrilldownChartView({
  rows,
  sortMode,
  segment1,
  segment2,
  segment3,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onBarDrawerClick,
}: DrilldownChartViewProps) {
  const { t } = useTranslation();
  const { wrapperRef, containerWidth } = useChartContainerWidth();
  const scrollContainerRef = useChartScrollPagination({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const metricLabel = METRIC_LABEL[sortMode];
  const segLabels = [
    segment1 !== SEGMENT_NONE ? segmentLabel(segment1) : "Creative",
    ...(segment2 !== SEGMENT_NONE ? [segmentLabel(segment2)] : []),
    ...(segment3 !== SEGMENT_NONE ? [segmentLabel(segment3)] : []),
  ];
  const title = `${metricLabel} by ${segLabels.join(", ")}`;

  const chartData = useMemo<ChartRow[]>(() => {
    const field = METRIC_FIELD[sortMode];
    return rows.map((row, i) => {
      const hash = extractString(row.hash);
      const competitor = extractString(row.competitor);
      const objectType = extractString(row.object_type);
      const carouselSlides = parseCarouselSlides(row, competitor);
      const isCarousel = Boolean(carouselSlides);
      const isVideo = !isCarousel && objectType.toLowerCase() !== "image";
      const ext = isVideo ? "mp4" : "png";
      return {
        label: String(i),
        value: Number(row[field] ?? 0),
        competitor,
        minDate: extractString(row.min_date),
        maxDate: extractString(row.max_date),
        thumbnailUrl:
          hash && competitor ? `${GCS_BASE}/${competitor}/thumbnails/${hash}.png` : "",
        assetUrl:
          hash && competitor ? `${GCS_BASE}/${competitor}/assets/${hash}.${ext}` : "",
        isVideo,
        isCarousel,
        carouselSlides,
        groupby1: extractString(row.Groupby1),
        groupby2: extractString(row.Groupby2),
        groupby3: extractString(row.Groupby3),
      };
    });
  }, [rows, sortMode]);

  if (rows.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-[14px] border bg-card p-[21px]"
        style={{ minHeight: "420px" }}
      >
        <p className="text-sm text-muted-foreground">No data to chart.</p>
      </div>
    );
  }

  const { chartWidth, plotAreaWidth } = useStickyChartWidth(containerWidth, chartData.length);
  // Plain function calls (not hooks) — safe to compute after the early return above,
  // same as useStickyChartWidth.
  const yAxisTicks = computeYAxisTicks(chartData);
  const yAxisDomain: [number, number] = [0, yAxisTicks[yAxisTicks.length - 1]!];

  return (
    <div ref={wrapperRef} className="rounded-[14px] border bg-card p-[21px]">
      <div className="mb-3">
        <p className="text-[15px] font-medium text-foreground">{title}</p>
        <p className="text-[12px] text-muted-foreground">
          {t(
            "drilldown.chart.interactionHint",
            "Hover a bar to inspect creative metadata. Click a bar to open details.",
          )}
        </p>
      </div>

      {/* Sticky Y axis (fixed, non-scrolling) + horizontally-scrolling bars/thumbnails */}
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <StickyYAxisChart data={chartData} tickFormatter={formatYTick} />

        <div
          ref={scrollContainerRef}
          className="min-w-0 flex-1 overflow-x-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-muted/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50"
          style={{ scrollbarWidth: "thin", scrollbarColor: "hsl(var(--border)) transparent" }}
        >
          {/* Outer flex row: [chart column, loader] */}
          <div style={{ display: "flex", alignItems: "stretch" }}>
            {/* Chart column: SVG on top, thumbnail HTML row below */}
            <div className="[&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-bar-rectangle]:outline-none [&_path]:outline-none [&_rect]:outline-none [&_*:focus]:outline-none [&_*:focus-visible]:outline-none" style={{ display: "flex", flexDirection: "column", width: chartWidth }}>
              <BarChart
                width={chartWidth}
                height={CHART_HEIGHT}
                data={chartData}
                margin={CHART_MARGIN}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                {/* Minimal XAxis — keeps recharts band scale but renders no ticks */}
                <XAxis
                  dataKey="label"
                  height={XAXIS_HEIGHT}
                  tick={false}
                  tickLine={false}
                  axisLine={false}
                />
                {/* Y axis rendered here too, but hidden — reserves the same plot-area
                    width as the sticky axis chart so bars/thumbnails stay aligned with
                    it. The visible axis lives in StickyYAxisChart above. */}
                <YAxis
                  orientation="left"
                  domain={yAxisDomain}
                  ticks={yAxisTicks}
                  tick={false}
                  tickLine={false}
                  axisLine={false}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  cursor={false}
                  content={(tooltipProps) => (
                    <DrilldownBarTooltip
                      {...(tooltipProps as TooltipContentProps<number, string>)}
                      metricLabel={metricLabel}
                      segment1={segment1}
                      segment2={segment2}
                      segment3={segment3}
                    />
                  )}
                />
                <Bar
                  dataKey="value"
                  fill="#6c4bff"
                  fillOpacity={0.85}
                  radius={[8, 8, 0, 0]}
                  activeBar={<DrilldownActiveBar />}
                  className={onBarDrawerClick ? "cursor-pointer" : undefined}
                  onClick={(_entry, index) => {
                    if (onBarDrawerClick && typeof index === "number" && rows[index]) {
                      onBarDrawerClick(rows[index], index)
                    }
                  }}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    formatter={(v) => formatYTick(Number(v ?? 0))}
                    style={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    offset={4}
                  />
                </Bar>
              </BarChart>

              {/* Thumbnail row — aligned with the bar plot area via matching left offset */}
              <div
                style={{
                  display: "flex",
                  width: plotAreaWidth,
                  marginLeft: CHART_MARGIN.left + YAXIS_WIDTH,
                  paddingTop: 8,
                  paddingBottom: 8,
                }}
              >
                {chartData.map((row, i) => (
                  <div
                    key={i}
                    style={{ flex: 1, display: "flex", justifyContent: "center" }}
                  >
                    <div
                      className="group relative"
                      style={{ width: THUMB_SIZE, height: THUMB_SIZE, flexShrink: 0 }}
                    >
                      <MediaPreview
                        src={row.isCarousel ? undefined : (row.assetUrl || undefined)}
                        thumbnailSrc={row.thumbnailUrl}
                        type={row.isVideo ? "video" : "image"}
                        alt={row.competitor}
                        title={row.competitor}
                        slides={row.carouselSlides ?? undefined}
                        className="size-full cursor-pointer overflow-hidden rounded-lg bg-muted"
                      />
                      {row.isCarousel ? (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <span className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                            <Layers className="size-3.5" />
                          </span>
                        </div>
                      ) : row.isVideo ? (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
                          <span className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="size-3.5" aria-hidden="true">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fetching-next-page spinner — sits to the right of the chart column */}
            {isFetchingNextPage && (
              <div className="flex items-center px-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
